import React, { Dispatch, FC, useState, useCallback } from 'react';
import cx from 'clsx';
import { CloverPaymentInput } from './CloverPaymentInput';

import styles from './styles.module.scss';

export type PaymentInfoErrors = {
  errors: Record<Clover.InputFieldType, string | undefined>;
  isAllTouched: boolean;
};

type PaymentInfoProps = {
  onChange: (newState: PaymentInfoErrors) => void;
  toggleShowErrors: Dispatch<boolean>;
  cloverElementsInstance: Clover.ElementRoot;
  toggleButtonDisableState: Dispatch<boolean>;
};

const areAllFieldsTouched = (state: Clover.ReturnedState) => Object.keys(state)
  .reduce((acc, key) => acc && state[key as Clover.InputFieldType].touched, true);

const getInputErrors = (state: Clover.ReturnedState): Record<Clover.InputFieldType, string | undefined> => Object.keys(state).reduce((acc, next) => ({
  ...acc,
  [next as Clover.InputFieldType]: state[next as Clover.InputFieldType].error
}), {} as Record<Clover.InputFieldType, string | undefined>);

// component starts here
const PaymentInfo: FC<PaymentInfoProps> = props => {
  const { cloverElementsInstance, onChange, toggleShowErrors, toggleButtonDisableState, children } = props;

  const [paymentFormErrorState, setPaymentFormErrorState] = useState<Partial<Record<Clover.InputFieldType, boolean>>>({
    CARD_NUMBER: false,
    CARD_CVV: false,
    CARD_DATE: false,
    CARD_POSTAL_CODE: false,
    // CARD_NAME: false,
  });

  const handleValidationChange = useCallback((fieldName: Clover.InputFieldType, state: Clover.ReturnedState) => {
    const newErrorState = getInputErrors(state);
    // if all field are touched
    // then the errors array should not have the current field's error, so set it to undefined
    const isAllTouched = areAllFieldsTouched(state);
    if (isAllTouched) {
      toggleButtonDisableState(!newErrorState[fieldName]);
      // eslint-disable-next-line
      newErrorState[fieldName] = undefined;
    } else {
      // all fields are not touched and current field has an error
      // then hide the error
      if (newErrorState[fieldName]) {
        // eslint-disable-next-line
        newErrorState[fieldName] = undefined;
      }
    }

    onChange({
      errors: newErrorState,
      isAllTouched,
    });

    setPaymentFormErrorState(prevState => ({ ...prevState, [fieldName]: false }));
  }, [onChange, toggleButtonDisableState]);

  const handleValidationOnBlur = useCallback((state: Clover.ReturnedState) => {
    const newPaymentFormErrorState = Object.keys(state).reduce((acc, next) => ({
      ...acc,
      [next]: state[next as Clover.InputFieldType].touched && !!state[next as Clover.InputFieldType].error
    }), {} as Record<Clover.InputFieldType, boolean>);

    setPaymentFormErrorState(newPaymentFormErrorState);

    const newErrorState = getInputErrors(state);

    onChange({
      errors: newErrorState,
      isAllTouched: areAllFieldsTouched(state)
    });

    toggleShowErrors(true);
  }, [onChange, toggleShowErrors]);

  return (
    <div className={styles.wrapper}>
      <div className={styles.paymentInfo}>
        <div className={styles.row}>
          {/*<CloverPaymentInput*/}
          {/*  cloverElementsInstance={cloverElementsInstance}*/}
          {/*  fieldName="CARD_NAME"*/}
          {/*  onValidationChange={handleValidationChange}*/}
          {/*  onValidationBlur={handleValidationOnBlur}*/}
          {/*  className={cx(styles.name, { [styles.error]: paymentFormErrorState['CARD_NAME'] })}*/}
          {/*/>*/}
          <CloverPaymentInput
            cloverElementsInstance={cloverElementsInstance}
            fieldName="CARD_NUMBER"
            onValidationChange={handleValidationChange}
            onValidationBlur={handleValidationOnBlur}
            className={cx(styles.number, { [styles.error]: paymentFormErrorState['CARD_NUMBER'] })}
          />
        </div>
        <div className={styles.row}>
          <CloverPaymentInput
            cloverElementsInstance={cloverElementsInstance}
            fieldName="CARD_DATE"
            onValidationChange={handleValidationChange}
            onValidationBlur={handleValidationOnBlur}
            className={cx(styles.date, { [styles.error]: paymentFormErrorState['CARD_DATE'] })}
          />
          <CloverPaymentInput
            cloverElementsInstance={cloverElementsInstance}
            fieldName="CARD_CVV"
            onValidationChange={handleValidationChange}
            onValidationBlur={handleValidationOnBlur}
            className={cx(styles.cvv, { [styles.error]: paymentFormErrorState['CARD_CVV'] })}
          />
          <CloverPaymentInput
            cloverElementsInstance={cloverElementsInstance}
            fieldName="CARD_POSTAL_CODE"
            onValidationChange={handleValidationChange}
            onValidationBlur={handleValidationOnBlur}
            className={cx(styles.zip, { [styles.error]: paymentFormErrorState['CARD_POSTAL_CODE'] })}
          />
        </div>
      </div>
      {children}
    </div>
  );
};

export { PaymentInfo };
